import {
  AggregateEnergyFlow,
  BatterySummary,
  Calculations,
  CostInputsPv,
  EnergyMatchConfig,
  getOnsitePpaLengthsConfig,
  getPpas,
  ProjectCosts,
  Site,
} from "@inrange/building-manager-api-client/models-site";
import {
  calcCfeScore,
  Column,
  SummaryBoxMetric,
} from "@inrange/theme-components";
import { ChartData } from "@inrange/theme-components/charts";
import {
  formatCurrency,
  formatCurrencyToNearestTen,
  formatLeaseTariff,
  formatLeaseTariffBillingFrequency,
  formatUnitsToNearestTen,
  tariffToFractionalCurrency,
} from "@inrange/theme-components/formatting";
import { InheritColorLink } from "@inrange/theme-components/marketplace";
import {
  LandlordSiteMetricValues,
  TenantSiteMetricValues,
} from "@inrange/theme-components/site";
import { Link } from "react-router-dom";
import {
  getProposalCostOfEnergyProcuredChartData,
  getProposalEnergySoldChartData,
  getProposalRevenueChartData,
} from "./chartDataFormatting";

interface SharedSite {
  id: string;
  name: string;
}

interface SummaryBox {
  header: string;
  metrics: SummaryBoxMetric[] | SummaryBoxMetric[][];
  grid: boolean;
  summaryChildren?: JSX.Element | (JSX.Element | undefined)[];
}

interface ChartDefinition {
  chartData: ChartData[];
  header: string;
  tooltipText: string;
  currency: boolean;
  showNotYetImportingMessaging?: boolean;
}

interface ProposalSummaryMetrics {
  metricCards?: TenantSiteMetricValues | LandlordSiteMetricValues;
  charts: ChartDefinition[];
  summaryBoxes: SummaryBox[];
}

const generateProposalSummaryMetrics = (
  proposalType: string,
  isGeneratingSite: boolean,
  site: Site,
  investmentModel: string,
  setInvestmentModel: (model: string) => void,
  hhDataUploaded: boolean,
  sharedSitesWithLandlord: SharedSite[],
  sellEnergyBlock: JSX.Element | undefined,
  generateBuyEnergyBlock: (unmetDemand: boolean) => JSX.Element | undefined,
  orgId: string,
  setShowEnergyCurvesModal: (showModal: boolean) => void,
  isSiteCanSell: boolean
): ProposalSummaryMetrics => {
  if (investmentModel === "none")
    return {
      metricCards: undefined,
      charts: [],
      summaryBoxes: [],
    };

  const {
    projectCosts,
    energyFlowAnnual: forecastEnergyFlowAnnual,
    totalBuildingArea,
    calculations,
    siteOwnerships,
  } = site;

  const hasOnsiteBehindMeter = forecastEnergyFlowAnnual.behindMeter > 0;
  const numNetworkImportSites = site.sdmMatches.filter(
    (match) => match.buyerId === site.id
  ).length;
  const numNetworkExportSites =
    site.sdmMatches.filter((match) => match.sellerId === site.id).length +
    (site.energyFlowAnnual.exported > 0 ? 1 : 0);

  const otherInvestmentModel =
    investmentModel === "license" ? "lease" : "license";
  const projectCostLeaseLicenseToggleText =
    investmentModel === "license"
      ? " for zero hardware, installation, and enabling works costs."
      : " to own the installation and earn more revenue.";

  const projectCostsLeaseLicenseToggle = (
    <Column
      $margin={"0 15px 15px 15px"}
      $padding={"0 8px"}
      $justifyContent={"flex-end"}
    >
      <div
        style={{
          fontSize: "16px",
        }}
      >
        <span
          style={{ textDecoration: "underline", cursor: "pointer" }}
          onClick={() => {
            setInvestmentModel(otherInvestmentModel);
            document.body.scrollTo({ top: 0, behavior: "smooth" });
          }}
        >
          Switch to {otherInvestmentModel}
        </span>
        <span>{projectCostLeaseLicenseToggleText}</span>
      </div>
    </Column>
  );
  const sharedSitesWithLandlordList = sharedSitesWithLandlord.length > 0 && (
    <Column
      $margin={"0 15px 15px 15px"}
      $padding={"0 8px"}
      $justifyContent={"flex-end"}
    >
      <div style={{ fontWeight: 550, fontSize: 18 }}>
        Other sites with landlord:
      </div>
      <ul style={{ marginTop: 0, paddingLeft: 20 }}>
        {sharedSitesWithLandlord.slice(0, 3).map((site, index) => {
          return (
            <li key={index}>
              <Link
                to={`/org/${orgId}/site/${site.id}`}
                style={{ color: "white" }}
                onClick={() => {
                  document.body.scrollTo({ top: 0, behavior: "smooth" });
                }}
              >
                {site.name}
              </Link>
            </li>
          );
        })}
        {sharedSitesWithLandlord.length > 3 && (
          <li>Plus {sharedSitesWithLandlord.length - 3} more sites...</li>
        )}
      </ul>
    </Column>
  );

  const isTenant = proposalType === "tenant";
  const isOwnerOccupier = proposalType === "ownerOccupier";
  const isLandlord = proposalType === "landlord";
  const isOwnerOccupierLicense =
    isOwnerOccupier && investmentModel === "license";
  const isOnsitePvLease =
    site.tariffConfig.onsitePPA.ppaPurchaseModel === "fixed_lease";

  const hhDemandDataString = hhDataUploaded
    ? ""
    : " Submitting your half hourly data will increase accuracy.";

  const costOfEnergyProcuredChart = {
    chartData: getProposalCostOfEnergyProcuredChartData(
      site,
      isOwnerOccupier,
      isOwnerOccupierLicense,
      investmentModel
    ),
    header: "Cost of energy procured by month",
    tooltipText: `The forecast cost of energy procured based on ${
      isGeneratingSite ? "on-site and network tariffs" : "the network tariff"
    } and forecast demand.${hhDemandDataString}`,
    currency: true,
  };
  const generateTariffMarketplaceBlock = (proposalType: string) => {
    switch (proposalType) {
      case "ownerOccupier":
        return [generateBuyEnergyBlock(false), sellEnergyBlock];
      case "landlord":
        return sellEnergyBlock;
      default:
        // "tenant"
        if (!isGeneratingSite) {
          return generateBuyEnergyBlock(false);
        }
    }
    return undefined;
  };

  const proposalSummaryMetrics: ProposalSummaryMetrics = {
    metricCards: generateSiteMetricValues(
      site,
      proposalType,
      hhDataUploaded,
      site.tariffConfig.supplier.peakOffPeak.overrideRatesMap["peak"]
        .tariff as unknown as number,
      site.tariffConfig.supplier.peakOffPeak.baseRate.tariff as unknown as
        | number
        | undefined,
      getOnsitePpaLengthsConfig(site),
      projectCosts,
      forecastEnergyFlowAnnual,
      site.energyFlowAnnual.procurement,
      isOwnerOccupierLicense
        ? site.financialModels.ooLicenseProcurement.costTotal
        : site.financialModels.procurement.costTotal,
      investmentModel,
      totalBuildingArea,
      calculations,
      hasOnsiteBehindMeter,
      numNetworkImportSites,
      numNetworkExportSites
    ),
    charts: [],
    summaryBoxes: [
      {
        header: isOnsitePvLease && isTenant ? "PV lease" : "Tariffs",
        metrics: generateTariffMetrics(
          site,
          proposalType,
          investmentModel,
          isGeneratingSite,
          isLandlord,
          isOnsitePvLease
        ),
        grid: true,
        summaryChildren: generateTariffMarketplaceBlock(proposalType),
      },
    ],
  };

  if (proposalType === "tenant") {
    if (!isOnsitePvLease) {
      proposalSummaryMetrics.charts.push(costOfEnergyProcuredChart);
    }
  } else {
    // Landlord or Owner Occupier
    if (isOnsitePvLease && site.hasAllZeroExportLimit) {
      // Don't show "Revenue by month" chart for onsite PV lease sites with zero export limit
    } else {
      proposalSummaryMetrics.charts.push({
        chartData: getProposalRevenueChartData(
          site,
          isOwnerOccupier,
          isOwnerOccupierLicense,
          investmentModel
        ),
        header: "Revenue by month",
        tooltipText: `The forecast revenue generated from energy sold (on-site or exported) after the given revenue share during a given month${investmentModel === "license" && !isOwnerOccupier ? " as well as monthly O&M, insurance, and other recurring costs" : ""}. Does not include VAT.`,
        currency: true,
      });
    }
    proposalSummaryMetrics.charts.push({
      chartData: getProposalEnergySoldChartData(site),
      header: "Energy sold by month",
      tooltipText:
        "The forecast amount of energy sold in kWh during a given month, where energy can be sold on-site or exported to the InRange network.",
      currency: false,
    });
  }

  if (isOwnerOccupier) {
    if (
      site.investmentModel !== "license" &&
      site.landlordLeaseRevenueShare === 0
    ) {
      // Don't show "Revenue by month" chart for OO sites with zero lease revshare, which are not
      // explicitly set to "license" investment model
      proposalSummaryMetrics.charts = proposalSummaryMetrics.charts.filter(
        (chart) => chart.header !== "Revenue by month"
      );
    }
    proposalSummaryMetrics.charts.push(
      investmentModel === "license"
        ? { ...costOfEnergyProcuredChart, showNotYetImportingMessaging: true }
        : costOfEnergyProcuredChart
    );
  }

  if (isTenant) {
    // Tenant
    const procurementBySourceSummary: SummaryBox = {
      header: "Procurement by source",
      metrics: generateProcurementMetricsSource(
        forecastEnergyFlowAnnual,
        numNetworkImportSites,
        setShowEnergyCurvesModal
      ),
      grid: false,
      summaryChildren: generateBuyEnergyBlock(true),
    };

    if (isGeneratingSite) {
      // Tenant: Generating
      if (!isOnsitePvLease) {
        proposalSummaryMetrics.summaryBoxes.push({
          header: "Procurement in year 1",
          metrics: generateProcurementMetricsMonetary(
            site,
            isOwnerOccupierLicense
          ),
          grid: false,
          summaryChildren: generateBuyEnergyBlock(false),
        });
      }
      proposalSummaryMetrics.summaryBoxes.push(procurementBySourceSummary);

      const landlordName = siteOwnerships
        .filter((so) => !so.isTest)
        .find((ownership) => ownership.ownership === "landlord")?.name;

      landlordName &&
        proposalSummaryMetrics.summaryBoxes.push({
          header: "Landlord",
          metrics: generateTenantLandlord(landlordName),
          grid: false,
          summaryChildren: sharedSitesWithLandlordList || undefined,
        });
    } else {
      // Tenant: Procurement only
      proposalSummaryMetrics.summaryBoxes.push(procurementBySourceSummary);
    }
  } else {
    // Landlord
    proposalSummaryMetrics.summaryBoxes.push(
      {
        header: "Project costs",
        metrics: generateProjectCostsMetrics(
          site.currencyCode,
          site.costInputsPv,
          site.totalBatterySummary,
          projectCosts,
          investmentModel
        ),
        grid: false,
        summaryChildren:
          site.operationalStatus === "notStarted"
            ? projectCostsLeaseLicenseToggle
            : undefined,
      },
      {
        header: "Energy sale by destination",
        metrics: generateEnergySaleMetrics(
          forecastEnergyFlowAnnual,
          numNetworkExportSites,
          setShowEnergyCurvesModal,
          isSiteCanSell
        ),
        grid: false,
        summaryChildren: sellEnergyBlock,
      }
    );
  }

  return proposalSummaryMetrics;
};

export default generateProposalSummaryMetrics;

const generateSiteMetricValues = (
  site: Site,
  proposalType: string,
  hhDataUploaded: boolean,
  marketTariff: number,
  marketTariffOffPeak: number | undefined,
  onsitePpaLengthConfig: EnergyMatchConfig,
  projectCosts: ProjectCosts,
  forecastEnergyFlowAnnual: AggregateEnergyFlow,
  forecastInRangeProcurement: number,
  forecastInRangeProcurementCost: number,
  investmentModel: string,
  totalBuildingArea: number,
  calculations: Calculations,
  hasOnsiteBehindMeter: boolean,
  numNetworkImportSites: number,
  numNetworkExportSites: number
): TenantSiteMetricValues | LandlordSiteMetricValues => {
  const demandFulfilledByInRange =
    forecastInRangeProcurement / forecastEnergyFlowAnnual.demand;
  if (proposalType === "tenant")
    return {
      isOnsitePvLease:
        site.tariffConfig.onsitePPA.ppaPurchaseModel === "fixed_lease",
      procurementCostYearOne: forecastInRangeProcurementCost,
      procuredEnergyYearOne: forecastInRangeProcurement,
      ppaLength: getPpas(onsitePpaLengthConfig, "other").map(
        (ppa) => ppa.ppaLength as unknown as number
      ),
      ppaSavings: site.financialModels.procurement.savingsPerPpa.reduce(
        (acc, ppa) => acc + ppa,
        0
      ),
      marketTariff,
      marketTariffOffPeak,
      totalDemandYearOne: forecastEnergyFlowAnnual.demand,
      demandFulfilledByInRange,
      emissionsAvoidedYearOne: calculations.emissionsAvoided.totalAvoidance,
      cfeScore: calcCfeScore(forecastEnergyFlowAnnual),
      hhDataUploaded,
      hasOnsiteBehindMeter,
      numNetworkImportSites,
      installedCapacity: site.totalInstalledCapacity,
      batterySummary: site.totalBatterySummary,
    };

  const isLicense = investmentModel === "license";
  const isOwnerOccupier = proposalType === "ownerOccupier";

  const revenueYearOne =
    site.financialModels[proposalType][investmentModel].revenue;
  const siteMetricsValues: LandlordSiteMetricValues = {
    isOnsitePvLease:
      site.tariffConfig.onsitePPA.ppaPurchaseModel === "fixed_lease",
    revenueYearOne: revenueYearOne,
    initialInvestment: isLicense ? projectCosts.initialInvestment : 0,
    lifetimeRevenue:
      site.financialModels[proposalType][investmentModel].lifetimeRevenue,
    totalProjectCost: isLicense ? projectCosts.total : 0,
    irr: site.financialModels[proposalType].license.irr,
    payback: site.financialModels[proposalType].license.paybackMonths,
    totalBuildingAreaM2: totalBuildingArea,
    onSiteDemandYearOne: forecastEnergyFlowAnnual.demand,
    marketTariff,
    marketTariffOffPeak,
    demandFulfilledByInRange,
    procurementCostYearOne: forecastInRangeProcurementCost,
    generationPotentialYearOne: forecastEnergyFlowAnnual.generation,
    onSiteGenerationConsumed: forecastEnergyFlowAnnual.tenantEnergyShare,
    emissionsAvoidedYearOne: calculations.emissionsAvoided.totalAvoidance,
    cfeScore: calcCfeScore(forecastEnergyFlowAnnual),
    investmentModel: investmentModel,
    hhDataUploaded,
    hasOnsiteBehindMeter,
    numNetworkImportSites,
    numNetworkExportSites,
    installedCapacity: site.totalInstalledCapacity,
    batterySummary: site.totalBatterySummary,
  };

  if (isOwnerOccupier) {
    siteMetricsValues.ppaLength = getPpas(onsitePpaLengthConfig, "other").map(
      (ppa) => ppa.ppaLength as unknown as number
    );
    if (isLicense) {
      siteMetricsValues.ppaSavings =
        site.financialModels.ooLicenseProcurement.savingsPerPpa.reduce(
          (acc, ppa) => acc + ppa,
          0
        );
    } else {
      siteMetricsValues.ppaSavings =
        site.financialModels.procurement.savingsPerPpa.reduce(
          (acc, ppa) => acc + ppa,
          0
        );
    }
  }

  return siteMetricsValues;
};

const generateTariffMetrics = (
  site: Site,
  proposalType: string,
  investmentModel: string,
  isGeneratingSite: boolean,
  isLandlord: boolean,
  isOnsitePvLease: boolean
): SummaryBoxMetric[] | SummaryBoxMetric[][] => {
  const hasWiredBuy = site.sdmMatches.some(
    (match) => match.buyerId === site.id && match.isWired
  );
  const onsiteIndexing =
    site.tariffConfig.onsitePPA.ppaType === "indexed"
      ? ` indexed ${(site.tariffConfig.onsitePPA.ppaIndex * 100).toFixed(1)}%/yr`
      : undefined;

  const tenantTariff =
    proposalType === "ownerOccupier" && investmentModel === "license"
      ? 0
      : site.tariffConfig.onsitePPA.tariff;
  const onsiteTariffBlock: SummaryBoxMetric = isOnsitePvLease
    ? {
        value: formatLeaseTariff(
          tenantTariff,
          isLandlord
            ? "annually"
            : site.tariffConfig.onsitePPA.ppaLeaseBillingFrequency,
          site.currencyCode
        ),
        valueSubText: formatLeaseTariffBillingFrequency(
          isLandlord
            ? "annually"
            : site.tariffConfig.onsitePPA.ppaLeaseBillingFrequency
        ),
        dataTestId: "test-tariff",
      }
    : {
        value: tariffToFractionalCurrency(tenantTariff, site.currencyCode),
        valueSubText:
          !isGeneratingSite && hasWiredBuy
            ? "Wired"
            : onsiteIndexing
              ? ["On-site", onsiteIndexing]
              : "On-site",
        dataTestId: "test-tariff",
      };

  const networkImportTariffBlock: SummaryBoxMetric = {
    value: tariffToFractionalCurrency(
      site.tariffConfig.networkImportTariff,
      site.currencyCode
    ),
    dataTestId: isGeneratingSite ? "" : "test-tariff",
    valueSubText: ["InRange network", "excl. non-commodity costs"],
  };

  const onsiteSaasCostBlock: SummaryBoxMetric = {
    value: tariffToFractionalCurrency(
      site.ownerOccupierLicenseInRangeSaasTariffRate,
      site.currencyCode
    ),
    valueSubText: "Management fee",
  };

  const networkExportTariffBlock: SummaryBoxMetric = {
    value: tariffToFractionalCurrency(
      site.blendedExportTariff,
      site.currencyCode
    ),
    valueSubText: "Avg. export",
    dataTestId: isGeneratingSite ? "" : "test-tariff",
  };

  if (!isGeneratingSite && !hasWiredBuy) {
    return [networkImportTariffBlock];
  }

  if (proposalType === "landlord") {
    if (
      site.energyFlowAnnual.exported === 0 &&
      site.energyFlowAnnual.networkExport === 0
    ) {
      // No export, so don't show avg export tariff
      return [onsiteTariffBlock];
    } else {
      return [onsiteTariffBlock, networkExportTariffBlock];
    }
  }

  if (proposalType === "tenant") {
    if (isOnsitePvLease) {
      return [onsiteTariffBlock];
    } else {
      return [onsiteTariffBlock, networkImportTariffBlock];
    }
  }

  const hasExport =
    site.energyFlowAnnual.exported > 0 ||
    site.energyFlowAnnual.networkExport > 0;

  if (investmentModel === "license") {
    // owner occupier license
    return [
      [onsiteTariffBlock, networkImportTariffBlock],
      hasExport
        ? [onsiteSaasCostBlock, networkExportTariffBlock]
        : [onsiteSaasCostBlock],
    ];
  }

  // owner occupier lease
  return hasExport
    ? [
        [onsiteTariffBlock, networkImportTariffBlock],
        [networkExportTariffBlock],
      ]
    : [onsiteTariffBlock, networkImportTariffBlock];
};

const generateProjectCostsMetrics = (
  currencyCode: string,
  costInputsPv: CostInputsPv,
  batterySummary: BatterySummary | undefined,
  projectCosts: ProjectCosts,
  investmentModel: string
): SummaryBoxMetric[] => {
  const metrics: SummaryBoxMetric[] = [];
  const isLicense = investmentModel === "license";
  if (isLicense) {
    metrics.push({
      value: formatCurrency(
        isLicense ? projectCosts.initialCostPerKWp : 0,
        currencyCode,
        0,
        false
      ),
      valueSubText: "Cost per kWp",
    });
  }
  metrics.push({
    value: formatCurrencyToNearestTen(
      isLicense ? projectCosts.pvHardware : 0,
      currencyCode,
      0,
      false
    ),
    valueSubText: "Hardware costs",
  });
  if (batterySummary) {
    metrics.push({
      value: formatCurrencyToNearestTen(
        isLicense
          ? projectCosts.batteryHardware + projectCosts.batteryInstallation
          : 0,
        currencyCode,
        0,
        false
      ),
      valueSubText: "Battery costs",
    });
  }
  metrics.push({
    value: formatCurrencyToNearestTen(
      isLicense
        ? projectCosts.pvInstallation +
            costInputsPv.enablingWorksCosts +
            costInputsPv.wiringCosts
        : 0,
      currencyCode,
      0,
      false
    ),
    valueSubText: "Installation + enabling works",
  });
  metrics.push({
    value: formatCurrencyToNearestTen(
      isLicense ? projectCosts.maintenance : 0,
      currencyCode,
      0,
      false
    ),
    valueSubText: "Lifetime O&M costs",
  });
  if (projectCosts.additionalCosts > 0) {
    metrics.push({
      value: formatCurrencyToNearestTen(
        isLicense ? projectCosts.additionalCosts : 0,
        currencyCode,
        0,
        false
      ),
      valueSubText: "Lifetime additional costs",
    });
  }

  return metrics;
};

const generateProcurementMetricsMonetary = (
  site: Site,
  isOwnerOccupierLicense: boolean
): SummaryBoxMetric[] => {
  const procurement = isOwnerOccupierLicense
    ? site.financialModels.ooLicenseProcurement
    : site.financialModels.procurement;
  const metrics: SummaryBoxMetric[] = [];
  if (site.energyFlowAnnual.behindMeterPV > 0) {
    metrics.push({
      value: formatCurrencyToNearestTen(
        procurement.costOnsitePV,
        site.currencyCode,
        0,
        false
      ),
      valueSubText: `On-site direct from PV`,
    });
  }
  if (site.energyFlowAnnual.behindMeterBattery > 0) {
    metrics.push({
      value: formatCurrencyToNearestTen(
        procurement.costOnsiteBattery,
        site.currencyCode,
        0,
        false
      ),
      valueSubText: `On-site from battery discharge`,
    });
  }
  if (site.energyFlowAnnual.behindMeterBatteryGrid > 0) {
    metrics.push({
      value: formatCurrencyToNearestTen(
        procurement.costOnsiteTariffArbitrage,
        site.currencyCode,
        0,
        false
      ),
      valueSubText: `On-site peak shifting`,
    });
  }
  metrics.push({
    value: formatCurrencyToNearestTen(
      procurement.costNetworkImport,
      site.currencyCode,
      0,
      false
    ),
    valueSubText: `InRange network`,
  });
  metrics.push({
    value: formatCurrencyToNearestTen(
      procurement.costTotal,
      site.currencyCode,
      0,
      false
    ),
    valueSubText: `Total`,
  });

  return metrics;
};

const generateProcurementMetricsSource = (
  forecastEnergyFlowAnnual: AggregateEnergyFlow,
  numNetworkImportSites: number,
  setShowEnergyCurvesModal: (showModal: boolean) => void
): SummaryBoxMetric[] => {
  const demand = forecastEnergyFlowAnnual.demand;
  const metrics: SummaryBoxMetric[] = [];
  if (forecastEnergyFlowAnnual.behindMeterPV > 0) {
    metrics.push({
      value: formatUnitsToNearestTen(
        forecastEnergyFlowAnnual.behindMeterPV,
        "kWh"
      ),
      valueSubText: `On-site direct from PV (${((forecastEnergyFlowAnnual.behindMeterPV / demand) * 100).toFixed(1)}%)`,
    });
  }
  if (forecastEnergyFlowAnnual.behindMeterBattery > 0) {
    metrics.push({
      value: formatUnitsToNearestTen(
        forecastEnergyFlowAnnual.behindMeterBattery,
        "kWh"
      ),
      valueSubText: `On-site from battery discharge (${((forecastEnergyFlowAnnual.behindMeterBattery / demand) * 100).toFixed(1)}%)`,
    });
  }
  if (forecastEnergyFlowAnnual.behindMeterBatteryGrid > 0) {
    metrics.push({
      value: formatUnitsToNearestTen(
        forecastEnergyFlowAnnual.behindMeterBatteryGrid,
        "kWh"
      ),
      valueSubText: `On-site peak shifting (${((forecastEnergyFlowAnnual.behindMeterBatteryGrid / demand) * 100).toFixed(1)}%)`,
    });
  }
  metrics.push({
    value: formatUnitsToNearestTen(
      forecastEnergyFlowAnnual.networkImport,
      "kWh"
    ),
    valueSubText: (
      <>
        InRange network (
        {((forecastEnergyFlowAnnual.networkImport / demand) * 100).toFixed(1)}%
        {numNetworkImportSites > 0 && (
          <>
            {" "}
            from{" "}
            <InheritColorLink
              href="#"
              onClick={() => setShowEnergyCurvesModal(true)}
            >
              {numNetworkImportSites} sites
            </InheritColorLink>
          </>
        )}
        )
      </>
    ),
  });
  metrics.push({
    value: formatUnitsToNearestTen(forecastEnergyFlowAnnual.gridImport, "kWh"),
    valueSubText: `Other suppliers (${((forecastEnergyFlowAnnual.gridImport / demand) * 100).toFixed(1)}%)`,
  });

  return metrics;
};

const generateEnergySaleMetrics = (
  forecastEnergyFlowAnnual: AggregateEnergyFlow,
  numNetworkExportSites: number,
  setShowEnergyCurvesModal: (showModal: boolean) => void,
  isSiteCanSell: boolean
): SummaryBoxMetric[] => {
  const behindMeterPercent =
    (forecastEnergyFlowAnnual.behindMeter /
      forecastEnergyFlowAnnual.generation) *
    100;
  const totalExport =
    forecastEnergyFlowAnnual.networkExport + forecastEnergyFlowAnnual.exported;
  const exportPercent =
    (totalExport / forecastEnergyFlowAnnual.generation) * 100;
  const metrics: SummaryBoxMetric[] = [
    {
      value: formatUnitsToNearestTen(
        forecastEnergyFlowAnnual.behindMeter,
        "kWh"
      ),
      valueSubText: `On-site (${behindMeterPercent.toFixed(1)}%)`,
    },
    {
      value: formatUnitsToNearestTen(totalExport, "kWh"),
      valueSubText: (
        <>
          InRange network ({exportPercent.toFixed(1)}%
          {numNetworkExportSites > 0 &&
            (isSiteCanSell ? (
              <>
                {" "}
                to{" "}
                <InheritColorLink
                  href="#"
                  onClick={() => setShowEnergyCurvesModal(true)}
                >
                  {numNetworkExportSites} site{numNetworkExportSites > 1 && "s"}
                </InheritColorLink>
              </>
            ) : (
              <>
                {" "}
                to {numNetworkExportSites} site
                {numNetworkExportSites > 1 && "s"}
              </>
            ))}
          )
        </>
      ),
    },
  ];

  return metrics;
};

const generateTenantLandlord = (
  landlordName: string | undefined
): SummaryBoxMetric[] => {
  return [{ value: landlordName ?? "N/A", valueSubText: undefined }];
};
